import { useState, useEffect } from 'react';
import { useCategories } from '../../../hooks/useCategories';
import { useAuthors } from '../../../hooks/useAuthors';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import Autocomplete from '../../../components/ui/Autocomplete';
import MultiSelectAutocomplete from '../../../components/ui/MultiSelectAutocomplete';
import ImageSourceModal from '../../../components/ui/ImageSourceModal';
import { useImageCapture } from '../../../hooks/useImageCapture.jsx';

const BookForm = ({ 
  editingBook = null, 
  onSave = () => {}, 
  onCancel = () => {},
  mode = 'create', // 'create', 'edit'
  addBook,
  updateBook,
  deleteBook
}) => {
  const { categories } = useCategories();
  const { authors } = useAuthors();
  
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    ranking: '',
    price: '',
    pages: '',
    language: '',
    available: true,
    categories: [],
    categoryInput: '',
    description: '',
    images: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Hook para captura de imágenes
  const {
    isSourceModalOpen,
    openImageSourceModal,
    closeImageSourceModal,
    handleImageSourceSelect,
    createHiddenInputs
  } = useImageCapture();

  // Cargar datos del libro en modo edición
  useEffect(() => {
    if (editingBook && mode === 'edit') {
      // Handle both old format and new format from get_all_books
      const bookData = editingBook.title_product ? editingBook : {
        // Map from get_all_books format to old format for compatibility
        title_product: editingBook.book_title,
        author_id: editingBook.book_author_id,
        rating_product: editingBook.book_rating,
        price_original_product: editingBook.book_price,
        pages_product: editingBook.book_pages,
        language_product: editingBook.book_language,
        available_product: editingBook.book_available,
        categories_product: editingBook.book_categories,
        description_product: editingBook.book_description,
        image_url_product: editingBook.book_images,
        id_product: editingBook.book_id
      };

      setFormData({
        title: bookData.title_product || '',
        author: bookData.author_id || '',
        ranking: bookData.rating_product || '',
        price: bookData.price_original_product || '',
        pages: bookData.pages_product || '',
        language: bookData.language_product || '',
        available: bookData.available_product === true,
        categories: bookData.categories_product || [],
        categoryInput: '',
        description: bookData.description_product || '',
        images: bookData.image_url_product || [],
      });
    }
  }, [editingBook, mode]);

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Limpiar mensajes previos
    setError(null);
    setSuccess(null);

    // Validar que al menos una imagen esté presente
    const processedImages = formData.images.filter(img => img);
    if (processedImages.length === 0) {
      setError('Debe agregar al menos una imagen del libro');
      return;
    }

    // Validar que al menos una categoría esté seleccionada
    if (formData.categories.length === 0) {
      setError('Debe seleccionar al menos una categoría');
      return;
    }

    setIsSubmitting(true);
    try {
      const processedFormData = {
        ...formData,
        images: processedImages,
        available_product: formData.available,
        author: formData.author || ''
      };

      if (mode === 'edit' && editingBook) {
        // Handle both old and new format for book ID
        const bookId = editingBook.id_product || editingBook.book_id;
        await updateBook(bookId, processedFormData);
        setSuccess('Libro actualizado exitosamente');
        
        // Cerrar modal y recargar página después de un breve delay
        setTimeout(() => {
          onSave();
          // Recargar catálogo principal si está disponible
          if (window.refreshBookCatalog) {
            window.refreshBookCatalog();
          }
          window.location.reload();
        }, 1500);
      } else {
        await addBook(processedFormData);
        setSuccess('Libro creado exitosamente');
        
        // Cerrar modal después de un breve delay para mostrar el mensaje
        setTimeout(() => {
          onSave();
          // Recargar catálogo principal si está disponible
          if (window.refreshBookCatalog) {
            window.refreshBookCatalog();
          }
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving book:', error);
      setError(error.message || 'Error al guardar el libro. Por favor, intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manejar eliminación
  const handleDelete = async () => {
    if (!editingBook || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Handle both old and new format for book ID
      const bookId = editingBook.id_product || editingBook.book_id;
      await deleteBook(bookId);
      // Recargar catálogo principal si está disponible
      if (window.refreshBookCatalog) {
        window.refreshBookCatalog();
      }
      onSave();
    } catch (error) {
      console.error('Error deleting book:', error);
    } finally {
      setIsSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  const [imageIndex, setImageIndex] = useState(0);

  // Función para convertir archivo a base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    if (name === 'images') {
      const selectedFiles = Array.from(files).slice(0, 5);
      
      // Convertir archivos a Data URLs (Base64)
      const imageUrls = await Promise.all(
        selectedFiles.map(file => convertToBase64(file))
      );
      
      setFormData((prev) => ({ ...prev, images: imageUrls }));
      setImageIndex(0);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };


  const handleCategorySelect = (e) => {
    const selected = e.target.value;
    if (selected && !formData.categories.includes(selected)) {
      setFormData((prev) => ({
        ...prev,
        categories: [...prev.categories, selected],
        categoryInput: '',
      }));
    }
  };


  const removeCategory = (index) => {
    const updated = formData.categories.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, categories: updated }));
  };

  // Función para remover imagen
  const removeImage = (index) => {
    const updatedImages = [...formData.images];
    updatedImages[index] = null;
    // Si se elimina la portada (índice 0), también eliminar del marco 1
    if (index === 0) {
      updatedImages[1] = null;
    }
    // Si se elimina del marco 1, también eliminar la portada
    if (index === 1) {
      updatedImages[0] = null;
    }
    setFormData((prev) => ({ ...prev, images: updatedImages }));
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        className="bg-white border border-slate-200 rounded-xl p-6 shadow-md space-y-6 max-w-5xl mx-auto"
      >
      {/* Mensajes de error y éxito */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <div className="text-red-600 text-sm">{error}</div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <div className="text-green-600 text-sm">{success}</div>
          </div>
        </div>
      )}

      {/* IMAGEN + CAMPOS LATERALES */}
      <div className="flex flex-wrap gap-6">
        {/* Marcos opcionales */}
        <div className="flex flex-wrap gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="relative w-[100px] h-[120px] border border-dashed border-slate-300 rounded-md bg-slate-50 overflow-hidden flex items-center justify-center"
            >
              <div
                onClick={() => openImageSourceModal((base64Url) => {
                  const updatedImages = [...formData.images];
                  if (i === 1) {
                    // Si es el marco 1 (Portada), actualizar también el índice 0
                    updatedImages[0] = base64Url;
                    updatedImages[1] = base64Url;
                  } else {
                    updatedImages[i] = base64Url;
                  }
                  setFormData((prev) => ({ ...prev, images: updatedImages }));
                })}
                className="absolute inset-0 cursor-pointer"
              />
              {(i === 1 ? formData.images[0] : formData.images[i]) ? (
                <>
                  <img
                    src={i === 1 ? formData.images[0] : formData.images[i]}
                    alt={`extra-${i}`}
                    className="w-full h-full object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold hover:bg-red-600 transition-colors shadow-md"
                  >
                    ×
                  </button>
                </>
              ) : (
                <>
                  <span className="text-slate-400 text-xl">+</span>
                  <span className="absolute bottom-1 text-[10px] text-slate-400">
                    {i === 1 ? 'Portada' : 'Opcional'}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Imagen de Portada */}
        <div className="relative w-[250px] h-[300px] border border-slate-300 rounded-md overflow-hidden flex items-center justify-center"
             style={{
               backgroundImage: 'url(/assets/images/background_image.png)',
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               backgroundRepeat: 'no-repeat'
             }}>
          <div
            onClick={() => openImageSourceModal((base64Url) => {
              const updatedImages = [...formData.images];
              updatedImages[0] = base64Url;
              updatedImages[1] = base64Url; // reflejo en primer marco opcional (Portada)
              setFormData((prev) => ({ ...prev, images: updatedImages }));
            })}
            className="absolute inset-0 cursor-pointer"
          />
          {formData.images[0] ? (
            <>
              <div className="relative w-[120px] h-[155px] flex items-center justify-center">
                <img
                  src={formData.images[0]}
                  alt="Imagen de Portada"
                  className="w-full h-full object-cover rounded shadow-lg"
                />
              </div>
              <button
                type="button"
                onClick={() => removeImage(0)}
                className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold hover:bg-red-600 transition-colors shadow-md"
              >
                ×
              </button>
            </>
          ) : (
            <span className="text-white text-sm pointer-events-none text-center px-4 bg-black bg-opacity-50 rounded p-2">
              Imagen de Portada
            </span>
          )}
        </div>

        {/* Inputs de Título a Autor */}
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'Título', name: 'title' },
            { label: 'Calificación', name: 'ranking', type: 'number' },
            { label: 'Precio', name: 'price', type: 'number' },
            { label: 'Páginas', name: 'pages', type: 'number' },
          ].map(({ label, name, type = 'text' }) => (
            <div key={name}>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {label}
              </label>
              <input
                type={type}
                name={name}
                value={formData[name]}
                onChange={handleChange}
                required
                placeholder={`Ingrese ${label.toLowerCase()}`}
                className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
              />
            </div>
          ))}

          {/* Idioma */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Idioma
            </label>
            <select
              name="language"
              value={formData.language}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
            >
              <option value="">Seleccionar idioma</option>
              <option value="Español">Español</option>
              <option value="Inglés">Inglés</option>
              <option value="Bilingüe">Bilingüe</option>
            </select>
          </div>

          {/* Autor */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Autor
            </label>
            <Autocomplete
              value={formData.author}
              onSelect={(authorId) => setFormData(prev => ({ ...prev, author: authorId }))}
              options={authors}
              placeholder="Escribir nombre del autor..."
              displayKey="name_author"
              valueKey="id_author"
              required
            />
          </div>
        </div>
      </div>


      {/* DESCRIPCIÓN */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Descripción
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          rows={4}
          className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800 resize-none"
          placeholder="Descripción del libro..."
        />
      </div>

      {/* DISPONIBILIDAD */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Disponibilidad
        </label>
        <select
          name="available"
          value={formData.available.toString()}
          onChange={(e) => setFormData(prev => ({ ...prev, available: e.target.value === 'true' }))}
          className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
        >
          <option value="true">Disponible</option>
          <option value="false">No disponible</option>
        </select>
      </div>

      {/* CATEGORÍAS */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Categorías
        </label>
        <MultiSelectAutocomplete
          selectedValues={formData.categories}
          onChange={(selectedCategories) => setFormData(prev => ({ ...prev, categories: selectedCategories }))}
          options={categories}
          placeholder="Escribir para buscar categorías..."
          displayKey="name_category"
          valueKey="id_category"
          required
          className="relative"
        />
      </div>


      {/* BOTONES */}
      <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary text-primary-foreground px-5 py-2.5 rounded-lg font-medium shadow-md hover:bg-primary/90 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed text-center"
          >
            {isSubmitting ? 'Guardando...' : (mode === 'edit' ? 'Actualizar libro' : 'Crear libro')}
          </button>
          
          <button
            type="button"
            onClick={onCancel}
            className="bg-slate-200 text-slate-700 px-5 py-2.5 rounded-lg font-medium shadow-md hover:bg-slate-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 text-center"
          >
            Cancelar
          </button>
        </div>

        {mode === 'edit' && editingBook && (
          <div className="w-full sm:w-auto">
            {showDeleteConfirm ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium shadow-md hover:bg-red-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 text-center"
                >
                  {isSubmitting ? 'Eliminando...' : 'Confirmar'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-medium shadow-md hover:bg-slate-300 transition-all duration-200 text-center"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full sm:w-auto bg-orange-600 text-white px-4 py-2 rounded-lg font-medium shadow-md hover:bg-orange-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 text-center"
              >
                Eliminar libro
              </button>
            )}
          </div>
        )}
      </div>
    </form>

    {/* Modal de confirmación de eliminación */}
    <ConfirmDeleteModal
      isOpen={showDeleteConfirm}
      onConfirm={handleDelete}
      onCancel={() => setShowDeleteConfirm(false)}
      title="¿Eliminar libro?"
      message="Esta acción no se puede deshacer."
      itemName={editingBook?.title_product || editingBook?.book_title}
      isLoading={isSubmitting}
    />

    {/* Modal de selección de fuente de imagen */}
    <ImageSourceModal
      isOpen={isSourceModalOpen}
      onClose={closeImageSourceModal}
      onSelectSource={handleImageSourceSelect}
    />

    {/* Inputs ocultos para captura de imágenes */}
    {createHiddenInputs()}
    </div>
  );
};

export default BookForm;
